import React from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipProvider } from "@/components/ui/tooltip";
import { Pencil, Shield, User, BadgeAlert, Trash2, CheckCircle2, XCircle, Bot, Zap } from "lucide-react";
import { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

export interface UserProfile {
  id: string;
  email: string | null;
  display_name: string | null;
  role: AppRole;
  is_verified: boolean;
  tos_accepted: boolean;
  is_profile_complete?: boolean; // Added profile completeness flag
}

interface UserRowProps {
  user: UserProfile;
  onEditUser: (user: UserProfile) => void;
  onDeleteUser: (user: UserProfile) => void;
}

export const UserRow: React.FC<UserRowProps> = ({ user, onEditUser, onDeleteUser }) => {
  const getRoleIcon = (role: AppRole) => {
    switch (role) {
      case "admin":
        return <Shield className="h-4 w-4 text-purple-600" />;
      case "vendor":
        return <BadgeAlert className="h-4 w-4 text-blue-600" />;
      case "auto_mock":
        return <Bot className="h-4 w-4 text-orange-600" />;
      case "auto_live":
        return <Zap className="h-4 w-4 text-green-600" />;
      default:
        return <User className="h-4 w-4 text-gray-600" />;
    }
  };

  // Determine if this is an auto-generated test user
  const isTestUser = user.role === 'auto_mock' || user.role === 'auto_live';
  
  return (
    <TableRow 
      key={user.id} 
      className={`${isTestUser ? 'bg-orange-50 hover:bg-orange-100' : 'hover:bg-gray-50'}`}
    >
      <TableCell className="font-medium">
        {isTestUser && (
          <span className="inline-block mr-2 px-1.5 py-0.5 text-xs bg-orange-100 text-orange-800 rounded">
            Test
          </span>
        )}
        {user.email || "-"}
      </TableCell>
      <TableCell>{user.display_name || "-"}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          {getRoleIcon(user.role)}
          <span className="capitalize">{user.role.replace('_', ' ')}</span>
        </div>
      </TableCell>
      <TableCell>
        <TooltipProvider>
          <div className="flex items-center gap-4">
            <Tooltip content="Email verification status">
              {user.is_verified ? (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
            </Tooltip>

            <Tooltip content="Terms of service acceptance">
              {user.tos_accepted ? (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
            </Tooltip>

            <Tooltip content="Profile completeness (address & phone)">
              {user.is_profile_complete ? (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
            </Tooltip>
          </div>
        </TooltipProvider>
      </TableCell>
      <TableCell>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEditUser(user)}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => onDeleteUser(user)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};
